import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  translations, 
  languages, 
  defaultLanguage, 
  LANGUAGE_STORAGE_KEY,
  createTranslator 
} from '../lib/translations';
import api from '../lib/api';
import { supabase } from '../lib/supabase';

const LanguageContext = createContext({});

export const LanguageProvider = ({ children }) => {
  // Initialize from localStorage or default
  const [language, setLanguageState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && translations[stored]) {
        return stored;
      }
    }
    return defaultLanguage;
  });
  const languageRef = useRef(language);

  // Create memoized translator function
  const t = useMemo(() => createTranslator(language), [language]);

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  // Set language and persist to localStorage
  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      // Dispatch custom event for components that need to react immediately
      window.dispatchEvent(new CustomEvent('languageChange', { detail: { language: lang } }));
      // Persist to backend (best-effort, non-blocking) so Lifecycle Emails render in the
      // user's chosen language. Silently no-ops when there's no active session (e.g. landing page).
      api.put('/user/profile', { locale: lang }).catch(() => {});
    }
  }, []);

  // On login (or first mount with an existing session), sync the canonical locale saved on
  // the backend profile. Pre-existing users without a saved locale get their current
  // frontend/localStorage choice persisted as the backend source of truth. Never overrides
  // an explicit later local choice with a stale event, and never breaks localStorage-only
  // behavior when the request fails or the user isn't authenticated.
  useEffect(() => {
    let cancelled = false;
    const syncLocaleFromBackend = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token || cancelled) return;
        const response = await api.get('/user/profile');
        if (cancelled) return;
        const backendLocale = response?.data?.locale;
        if (backendLocale && translations[backendLocale] && backendLocale !== languageRef.current) {
          setLanguageState(backendLocale);
          localStorage.setItem(LANGUAGE_STORAGE_KEY, backendLocale);
        } else if (!backendLocale) {
          api.put('/user/profile', { locale: languageRef.current }).catch(() => {});
        }
      } catch (e) {
        // Not authenticated or request failed - keep current localStorage-only behavior
      }
    };

    syncLocaleFromBackend();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        syncLocaleFromBackend();
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  // Get current language config
  const currentLanguage = useMemo(() => {
    return languages.find(l => l.code === language) || languages[0];
  }, [language]);

  // Sync with localStorage changes (for multi-tab support)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === LANGUAGE_STORAGE_KEY && e.newValue && translations[e.newValue]) {
        setLanguageState(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
    currentLanguage,
    languages,
    isPortuguese: language === 'ptBR',
    isEnglish: language === 'en',
  }), [language, setLanguage, t, currentLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to access language context
 * @returns {Object} Language context with t() function, language, setLanguage, etc.
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

/**
 * Hook for translation only (lighter weight)
 * @returns {Function} Translation function t(key, fallback)
 */
export const useTranslation = () => {
  const { t } = useLanguage();
  return t;
};

export default LanguageContext;
